import {useState, useEffect, useCallback, useMemo} from 'react';
import {useLazySearchUsersQuery} from '@api';
import {UserMinimal} from '@types';

type UseUsernameAutocompleteResult = {
    suggestions: UserMinimal[];
    isLoading: boolean;
    showSuggestions: boolean;
    selectedIndex: number;
    onInputChange: (value: string, selectionStart: number) => void;
    onKeyDown: (event: React.KeyboardEvent) => boolean;
    onSuggestionClick: (suggestion: UserMinimal) => void;
    setSelectedIndex: (index: number) => void;
    currentQuery: string;
    atPosition: number;
};

export const useUsernameAutocomplete = (
    value: string,
    onChange: (value: string) => void,
    textareaRef: React.RefObject<HTMLTextAreaElement | null>,
    contextUsers: UserMinimal[] = []
): UseUsernameAutocompleteResult => {
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [currentQuery, setCurrentQuery] = useState('');
    const [atPosition, setAtPosition] = useState(-1);

    const [searchUsers, {data: usersData, isLoading}] =
        useLazySearchUsersQuery();

    // Deduplicated context users
    const uniqueContextUsers = useMemo(() => {
        const seen = new Set<number>();
        const result: UserMinimal[] = [];
        for (const user of contextUsers) {
            if (!seen.has(user.id)) {
                seen.add(user.id);
                result.push(user);
            }
        }
        return result;
    }, [contextUsers]);

    const apiSuggestions: UserMinimal[] = useMemo(() => {
        return usersData?.entries ? usersData.entries : [];
    }, [usersData]);

    const suggestions: UserMinimal[] = useMemo(() => {
        if (currentQuery.length === 0) {
            return uniqueContextUsers;
        }
        return apiSuggestions;
    }, [currentQuery.length, uniqueContextUsers, apiSuggestions]);

    const showSuggestions = useMemo(() => {
        if (atPosition < 0) return false;
        if (currentQuery.length === 0) {
            return uniqueContextUsers.length > 0;
        }
        return (
            currentQuery.length >= 2 && (isLoading || suggestions.length > 0)
        );
    }, [
        atPosition,
        currentQuery.length,
        uniqueContextUsers.length,
        isLoading,
        suggestions.length,
    ]);

    const onInputChange = useCallback(
        (newValue: string, selectionStart: number) => {
            const beforeCursor = newValue.slice(0, selectionStart);
            const atMatch = beforeCursor.match(/@([a-zA-Z0-9_]*)$/);

            if (atMatch) {
                const query = atMatch[1];
                const position = selectionStart - query.length - 1;
                setCurrentQuery(query);
                setAtPosition(position);
                setSelectedIndex(-1);

                if (query.length >= 2) {
                    void searchUsers({q: query, limit: 20});
                }
            } else {
                setCurrentQuery('');
                setAtPosition(-1);
            }
        },
        [searchUsers]
    );

    const onSuggestionClick = useCallback(
        (suggestion: UserMinimal) => {
            if (atPosition >= 0) {
                const beforeAt = value.slice(0, atPosition);
                const afterQuery = value.slice(
                    atPosition + 1 + currentQuery.length
                );
                const newValue = `${beforeAt}@${suggestion.name}${afterQuery}`;
                onChange(newValue);

                const newCursorPos = atPosition + 1 + suggestion.name.length;
                setTimeout(() => {
                    if (textareaRef.current) {
                        textareaRef.current.setSelectionRange(
                            newCursorPos,
                            newCursorPos
                        );
                        textareaRef.current.focus();
                    }
                }, 0);
            }

            setSelectedIndex(-1);
            setCurrentQuery('');
            setAtPosition(-1);
        },
        [value, onChange, atPosition, currentQuery, textareaRef]
    );

    const onKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (!showSuggestions || suggestions.length === 0) {
                return false;
            }

            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    setSelectedIndex(prev =>
                        prev < suggestions.length - 1 ? prev + 1 : 0
                    );
                    return true;
                case 'ArrowUp':
                    event.preventDefault();
                    setSelectedIndex(prev =>
                        prev > 0 ? prev - 1 : suggestions.length - 1
                    );
                    return true;
                case 'Enter':
                    if (selectedIndex >= 0) {
                        event.preventDefault();
                        onSuggestionClick(suggestions[selectedIndex]);
                        return true;
                    }
                    break;
                case 'Tab': {
                    event.preventDefault();
                    const indexToSelect =
                        selectedIndex >= 0 ? selectedIndex : 0;
                    onSuggestionClick(suggestions[indexToSelect]);
                    return true;
                }
                case 'Escape':
                    setSelectedIndex(-1);
                    setCurrentQuery('');
                    setAtPosition(-1);
                    return true;
            }
            return false;
        },
        [showSuggestions, suggestions, selectedIndex, onSuggestionClick]
    );

    useEffect(() => {
        setSelectedIndex(0);
    }, [suggestions]);

    return {
        suggestions,
        isLoading,
        showSuggestions,
        selectedIndex,
        onInputChange,
        onKeyDown,
        onSuggestionClick,
        setSelectedIndex,
        currentQuery,
        atPosition,
    };
};
